import classNames from 'classnames';
import fp from 'lodash/fp';
import React from 'react';
import { graphql } from 'react-apollo';
import { Alert, Button, Col, Container, Dropdown, DropdownMenu, DropdownToggle, FormGroup, Row } from 'reactstrap';
import { branch, compose, lifecycle, pure, withHandlers, withProps, withStateHandlers } from 'recompose';
import { eventValueHandler, noCapMap } from '../../../../common';
import { artistTypes, genders, routes } from '../../../../constants';
import { handleInvalidForm } from '../../../../services';
import { FormControlItem } from '../../../composed';
import { hideIfNoArtistData, notFoundIfNoArtistData, withState } from '../../../higher-order';
import { Icon } from '../../../proxy';
import artistMutation from './artist-mutation.gql';
import artistQuery from './artist-query.gql';
import './artist.css';

const Artist = ({
  artist,
  artistTypesIsOpen,
  artistUpdateHandler,
  componentRef,
  error,
  hideMessage,
  message,
  onSubmit,
  setArtistTypesIsOpen,
  showMessage,
  submitted,
  updateArtist
}) => (
    <section className='artist flex-center'>
      <Container className='mt-5'>
        <form
          className={classNames('mb-4', { 'was-validated': submitted })}
          noValidate
          onSubmit={onSubmit}
          ref={(formRef) => componentRef && (componentRef.formRef = formRef)}>
          <Alert
            color={error ? 'danger' : 'success'}
            className='mb-5'
            isOpen={showMessage}
            toggle={hideMessage}>
            {error || message}
          </Alert>
          <FormControlItem
            id='name'
            onChange={artistUpdateHandler('name')}
            required
            value={artist.name}>
            <div className='invalid-feedback ml-5'>
              Name is required.
            </div>
          </FormControlItem>
          <Row>
            <Col xs={12} sm={6}>
              <FormControlItem
                id='first-name'
                onChange={artistUpdateHandler('firstName')}
                required
                value={artist.firstName}>
                <div className='invalid-feedback ml-5'>
                  First name is required.
                </div>
              </FormControlItem>
            </Col>
            <Col xs={12} sm={6}>
              <FormControlItem
                id='last-name'
                onChange={artistUpdateHandler('lastName')}
                required
                value={artist.lastName}>
                <div className='invalid-feedback ml-5'>
                  Last name is required.
                </div>
              </FormControlItem>
            </Col>
          </Row>
          <Row>
            <Col xs={12} sm={6}>
              <FormGroup>
                <input
                  id='international'
                  type='checkbox'
                  checked={artist.international}
                  onChange={() => updateArtist({ path: 'international', value: !artist.international })} />
                <label htmlFor='international'>
                  International
                </label>
              </FormGroup>
            </Col>
            <Col xs={12} sm={6}>
              <FormGroup>
                <input
                  id='leading'
                  type='checkbox'
                  checked={artist.leading}
                  onChange={() => updateArtist({ path: 'leading', value: !artist.leading })} />
                <label htmlFor='leading'>
                  Leading
                </label>
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col xs={12} sm={6}>
              <FormControlItem
                id='current-continent'
                onChange={artistUpdateHandler('currentLocation.continent')}
                value={artist.currentLocation.continent} />
            </Col>
            <Col xs={12} sm={6}>
              <FormControlItem
                id='current-country'
                onChange={artistUpdateHandler('currentLocation.country')}
                required
                value={artist.currentLocation.country}>
                <div className='invalid-feedback ml-5'>
                  Current Country is required.
                </div>
              </FormControlItem>
            </Col>
          </Row>
          <Row>
            <Col xs={12} sm={6}>
              <FormControlItem
                id='current-state'
                onChange={artistUpdateHandler('currentLocation.state')}
                value={artist.currentLocation.state} />
            </Col>
            <Col xs={12} sm={6}>
              <FormControlItem
                id='current-city'
                onChange={artistUpdateHandler('currentLocation.city')}
                value={artist.currentLocation.city} />
            </Col>
          </Row>
          <Row>
            <Col xs={12} sm={6}>
              <FormControlItem
                id='original-continent'
                onChange={artistUpdateHandler('originalLocation.continent')}
                value={artist.originalLocation.continent} />
            </Col>
            <Col xs={12} sm={6}>
              <FormControlItem
                id='original-country'
                onChange={artistUpdateHandler('originalLocation.country')}
                value={artist.originalLocation.country} />
            </Col>
          </Row>
          <Row>
            <Col xs={12} sm={6}>
              <FormControlItem
                id='original-state'
                onChange={artistUpdateHandler('originalLocation.state')}
                value={artist.originalLocation.state} />
            </Col>
            <Col xs={12} sm={6}>
              <FormControlItem
                id='original-city'
                onChange={artistUpdateHandler('originalLocation.city')}
                value={artist.originalLocation.city} />
            </Col>
          </Row>
          <FormControlItem
            id='bio'
            className='md-textarea'
            onChange={artistUpdateHandler('bio', fp.escape)}
            required
            tag='textarea'
            type={null}
            value={fp.unescape(artist.bio)}
            formControlProps={{ size: 8 }}>
            <div className='invalid-feedback ml-5'>
              Bio is required.
            </div>
          </FormControlItem>
          <FormControlItem
            id='profile-image'
            onChange={artistUpdateHandler('profileImage')}
            required
            value={artist.profileImage}>
            <div className='invalid-feedback ml-5'>
              Profile Image is required.
            </div>
          </FormControlItem>
          <h6>Other Images</h6>
          <Row>
            {
              noCapMap((image, index) =>
                <Col key={index} xs={12} sm={6}>
                  <FormControlItem
                    id={`other-image-${index}`}
                    className='remove'
                    label={null}
                    onChange={artistUpdateHandler(`otherImages[${index}]`)}
                    value={image} />
                </Col>
              )(artist.otherImages)
            }
            {
              fp.last(artist.otherImages) !== '' ?
                <Col xs={12} sm={6} className='flex-center'>
                  <Button
                    color='primary'
                    onClick={() =>
                      updateArtist({
                        path: 'otherImages',
                        value: [...artist.otherImages, '']
                      })
                    }>
                    <Icon icon='plus' />
                  </Button>
                </Col> :
                null
            }
          </Row>
          <Row className='mt-2 mb-4'>
            <Col xs={12} sm={6} className='flex-center'>
              <Dropdown isOpen={artistTypesIsOpen} toggle={() => setArtistTypesIsOpen(!artistTypesIsOpen)}>
                <DropdownToggle caret color='primary'>
                  Artist Types
                </DropdownToggle>
                <DropdownMenu>
                  {
                    fp.flow(
                      fp.values,
                      fp.map((value) => ({ selected: fp.includes(value)(artist.artistTypes), value })),
                      fp.map(({ selected, value }) => (
                        <FormGroup key={value}>
                          <input
                            id={value}
                            type='checkbox'
                            checked={selected}
                            onChange={() =>
                              updateArtist({
                                path: 'artistTypes',
                                value: (selected ?
                                  fp.remove(fp.partial(fp.eq, [value])) :
                                  fp.concat(value)
                                )(artist.artistTypes)
                              })} />
                          <label htmlFor={value}>
                            {fp.upperFirst(value)}
                          </label>
                        </FormGroup>
                      ))
                    )(artistTypes)
                  }
                </DropdownMenu>
              </Dropdown>
            </Col>
            <Col xs={12} sm={6} className='flex-center form-inline'>
              <FormGroup>
                <input
                  id='gender-male'
                  name='gender'
                  type='radio'
                  checked={artist.gender === genders.male}
                  onChange={() => updateArtist({ path: 'gender', value: 'male' })} />
                <label htmlFor='gender-male'>
                  Male
                </label>
              </FormGroup>
              <FormGroup>
                <input
                  id='gender-female'
                  name='gender'
                  type='radio'
                  checked={artist.gender === genders.female}
                  onChange={() => updateArtist({ path: 'gender', value: 'female' })} />
                <label htmlFor='gender-female'>
                  Female
                </label>
              </FormGroup>
            </Col>
          </Row>
          <FormControlItem
            id='website'
            onChange={artistUpdateHandler('website')}
            value={artist.website} />
          <Row>
            <Col xs={12} sm={6}>
              <FormControlItem
                id='facebook-profile'
                onChange={artistUpdateHandler('socialLinks.facebook.profile')}
                value={artist.socialLinks.facebook.profile} />
            </Col>
            <Col xs={12} sm={6}>
              <FormControlItem
                id='facebook-page'
                onChange={artistUpdateHandler('socialLinks.facebook.page')}
                value={artist.socialLinks.facebook.page} />
            </Col>
          </Row>
          <Row>
            <Col xs={12} sm={6}>
              <FormControlItem
                id='spotify'
                onChange={artistUpdateHandler('socialLinks.spotify')}
                value={artist.socialLinks.spotify} />
            </Col>
            <Col xs={12} sm={6}>
              <FormControlItem
                id='instagram'
                onChange={artistUpdateHandler('socialLinks.instagram')}
                value={artist.socialLinks.instagram} />
            </Col>
          </Row>
          <Row>
            <Col xs={12} sm={6}>
              <FormControlItem
                id='youtube'
                onChange={artistUpdateHandler('socialLinks.youtube')}
                value={artist.socialLinks.youtube} />
            </Col>
            <Col xs={12} sm={6}>
              <FormControlItem
                id='twitter'
                onChange={artistUpdateHandler('socialLinks.twitter')}
                value={artist.socialLinks.twitter} />
            </Col>
          </Row>
          <h6>Styles</h6>
          <Row>
            {
              noCapMap((style, index) =>
                <Col key={index} xs={12} sm={6}>
                  <FormControlItem
                    id={`style-${index}`}
                    className='remove'
                    label={null}
                    onChange={artistUpdateHandler(`styles[${index}]`)}
                    value={style}>
                    <Button
                      color='red'
                      className='btn-sm suffix'
                      onClick={() =>
                        updateArtist({
                          path: 'styles',
                          value: fp.pullAt([index])(artist.styles)
                        })}>
                      <Icon icon='close' />
                    </Button>
                  </FormControlItem>
                </Col>
              )(artist.styles)
            }
            {
              fp.last(artist.styles) !== '' ?
                <Col xs={12} sm={6} className='flex-center'>
                  <Button
                    color='primary'
                    onClick={() =>
                      updateArtist({
                        path: 'styles',
                        value: [...artist.styles, '']
                      })
                    }>
                    <Icon icon='plus' />
                  </Button>
                </Col> :
                null
            }
          </Row>
          <Row>
            <Col xs={12} sm={6}>
              <FormControlItem
                id='email'
                onChange={artistUpdateHandler('email')}
                value={artist.email} />
            </Col>
            <Col xs={12} sm={6}>
              <FormControlItem
                id='phone'
                onChange={artistUpdateHandler('phone')}
                value={artist.phone} />
            </Col>
          </Row>
          <h6>Videos</h6>
          <Row>
            {
              noCapMap((video, index) =>
                <Col key={index} xs={12} sm={6}>
                  <FormControlItem
                    id={`video-${index}`}
                    className='remove'
                    label={null}
                    onChange={artistUpdateHandler(`videos[${index}]`)}
                    value={video}>
                    <Button
                      color='red'
                      className='btn-sm suffix'
                      onClick={() =>
                        updateArtist({
                          path: 'videos',
                          value: fp.pullAt([index])(artist.videos)
                        })}>
                      <Icon icon='close' />
                    </Button>
                  </FormControlItem>
                </Col>
              )(artist.videos)
            }
            {
              fp.last(artist.videos) !== '' ?
                <Col xs={12} sm={6} className='flex-center'>
                  <Button
                    color='primary'
                    onClick={() =>
                      updateArtist({
                        path: 'videos',
                        value: [...artist.videos, '']
                      })
                    }>
                    <Icon icon='plus' />
                  </Button>
                </Col> :
                null
            }
          </Row>
          <h6>Partners</h6>
          <Row>
            {
              noCapMap((partner, index) =>
                <Col key={index} xs={12} sm={6}>
                  <FormControlItem
                    id={`partner-${index}`}
                    className='remove'
                    label={null}
                    onChange={artistUpdateHandler(`partners[${index}]`)}
                    value={partner}>
                    <Button
                      color='red'
                      className='btn-sm suffix'
                      onClick={() =>
                        updateArtist({
                          path: 'partners',
                          value: fp.pullAt([index])(artist.partners)
                        })}>
                      <Icon icon='close' />
                    </Button>
                  </FormControlItem>
                </Col>
              )(artist.partners)
            }
            {
              fp.overSome([
                fp.isEmpty,
                fp.flow(
                  fp.last,
                  fp.negate(fp.isEmpty)
                )
              ])(artist.partners) ?
                <Col xs={12} sm={6} className='flex-center'>
                  <Button
                    color='primary'
                    onClick={() =>
                      updateArtist({
                        path: 'partners',
                        value: [...artist.partners, '']
                      })
                    }>
                    <Icon icon='plus' />
                  </Button>
                </Col> :
                null
            }
          </Row>
          <Row>
            <Col className='text-center'>
              <Button color='primary' type='submit'>
                Submit
              </Button>
            </Col>
          </Row>
        </form>
      </Container>
    </section>
  );

const omitEmptyFields = (obj) =>
  fp.flow(
    fp.mapValues((value) =>
      fp.isPlainObject(value) ?
        omitEmptyFields(value) :
        value
    ),
    fp.omitBy(
      fp.overEvery([
        fp.negate(fp.isBoolean),
        fp.isEmpty
      ])
    )
  )(obj);

const artistState = (artist) => ({
  name: fp.getOr('')('name')(artist),
  firstName: fp.getOr('')('firstName')(artist),
  lastName: fp.getOr('')('lastName')(artist),
  international: fp.getOr(false)('international')(artist),
  leading: fp.getOr(false)('leading')(artist),
  currentLocation: {
    continent: fp.getOr('')('currentLocation.continent')(artist),
    country: fp.getOr('')('currentLocation.country')(artist),
    state: fp.getOr('')('currentLocation.state')(artist),
    city: fp.getOr('')('currentLocation.city')(artist)
  },
  originalLocation: {
    continent: fp.getOr('')('originalLocation.continent')(artist),
    country: fp.getOr('')('originalLocation.country')(artist),
    state: fp.getOr('')('originalLocation.state')(artist),
    city: fp.getOr('')('originalLocation.city')(artist)
  },
  bio: fp.getOr('')('bio')(artist),
  profileImage: fp.getOr('')('profileImage')(artist),
  otherImages: fp.getOr([])('otherImages')(artist),
  artistTypes: fp.getOr(['dancer'])('artistTypes')(artist),
  gender: fp.getOr('')('gender')(artist),
  website: fp.getOr('')('website')(artist),
  socialLinks: {
    facebook: {
      profile: fp.getOr('')('socialLinks.facebook.profile')(artist),
      page: fp.getOr('')('socialLinks.facebook.page')(artist)
    },
    spotify: fp.getOr('')('socialLinks.spotify')(artist),
    instagram: fp.getOr('')('socialLinks.instagram')(artist),
    youtube: fp.getOr('')('socialLinks.youtube')(artist),
    twitter: fp.getOr('')('socialLinks.twitter')(artist)
  },
  styles: fp.getOr(['zouk'])('styles')(artist),
  email: fp.getOr('')('email')(artist),
  phone: fp.getOr('')('phone')(artist),
  videos: fp.getOr([])('videos')(artist),
  partners: fp.flow(
    fp.getOr([])('partners'),
    fp.map(fp.get('name'))
  )(artist)
});

export default compose(
  lifecycle({
    componentDidMount() {
      this.setState({ componentRef: this });
    }
  }),
  branch(
    ({ match: { url } }) => url !== routes.adminPortal.createArtist,
    compose(
      withProps(({ match: { params: { id } } }) => ({
        id
      })),
      graphql(artistQuery),
      hideIfNoArtistData('data'),
      notFoundIfNoArtistData('data')
    )
  ),
  withStateHandlers(({
    data: {
      artist
    } = {}
  }) => ({
    artist: fp.flow(
      omitEmptyFields,
      artistState
    )(artist)
  }), {
      updateArtist: ({
        artist
      }) => ({
        path,
        value
      }) => ({
        artist: fp.set(path)(
          fp.isFunction(value) ?
            value(artist) :
            value
        )({ ...artist })
      })
    }
  ),
  graphql(artistMutation, {
    options: { errorPolicy: 'all' },
    props: ({ mutate }) => ({
      putArtist: (artist) => {
        mutate({
          variables: {
            artist: omitEmptyFields(artist)
          }
        })
      }
    })
  }),
  withState({
    artistTypesIsOpen: false,
    message: null,
    submitted: false,
    showMessage: false
  }),
  withHandlers({
    artistUpdateHandler: ({ updateArtist }) =>
      (path, transform) => eventValueHandler(
        (value) => updateArtist({
          path,
          value: transform ?
            transform(value) :
            value
        })
      ),
    onSubmit: ({
      artist,
      componentRef,
      putArtist,
      setMessage,
      setShowMessage,
      setSubmitted
    }) =>
      (e) => {
        e.preventDefault();
        e.stopPropagation();

        setSubmitted(true);

        if (e.target.checkValidity()) {
          putArtist(artist);
        } else if (componentRef && componentRef.formRef) {
          handleInvalidForm(componentRef.formRef);
        }
      },
    hideMessage: ({ setShowMessage }) => () => setShowMessage(false)
  }),
  pure
)(Artist);



// WEBPACK FOOTER //
// ./src/components/pages/admin-portal/artist/artist.js